import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import { ModeSelector } from '@/components/ModeSelector/ModeSelector';
import { useArticles } from '@/hooks/useArticles';
import { contentService, type FeatureMode, type HoroscopeRunResult } from '@/services/content';
import { horoscopoApi, type HoroscopeTemplate } from '@/services/horoscopo';
import type { ArticleSummary } from '@/types/article';

import styles from './Horoscopo.module.css';

type StatusFilter = 'todos' | 'publish' | 'draft';

const STATUS_CHIPS: { v: StatusFilter; label: string }[] = [
  { v: 'todos', label: 'Todos' },
  { v: 'publish', label: 'Publicados' },
  { v: 'draft', label: 'Rascunhos' },
];

function stripHtml(s: string | null): string {
  if (!s) return '';
  return s.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

function dayKey(a: ArticleSummary): string {
  return (a.published_at || '').slice(0, 10);
}

function fmtDay(key: string): string {
  if (!key) return '—';
  const d = new Date(`${key}T12:00:00`);
  return d.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });
}

function matchTemplate(a: ArticleSummary, tpl: HoroscopeTemplate): boolean {
  try {
    return new RegExp(tpl.match, 'i').test(a.title);
  } catch {
    return false;
  }
}

interface DaySlots {
  key: string;
  slots: { tpl: HoroscopeTemplate; article: ArticleSummary | null }[];
  extras: ArticleSummary[];
  published: number;
}

export function Horoscopo() {
  const [status, setStatus] = useState<StatusFilter>('todos');
  const [runResult, setRunResult] = useState<HoroscopeRunResult | null>(null);

  const featuresQuery = useQuery({
    queryKey: ['content', 'features'],
    queryFn: contentService.getFeatures,
  });
  const setMode = useMutation({
    mutationFn: (mode: FeatureMode) => contentService.setFeature('horoscope', { mode }),
    onSuccess: () => featuresQuery.refetch(),
  });
  const runDay = useMutation({
    mutationFn: () => contentService.horoscopeRun({ dry_run: true }),
    onSuccess: (r) => setRunResult(r),
  });
  const setImage = useMutation({
    mutationFn: (image: boolean) => contentService.setFeature('horoscope', { image }),
    onSuccess: () => featuresQuery.refetch(),
  });

  const tplQuery = useQuery({
    queryKey: ['horoscopo', 'templates'],
    queryFn: horoscopoApi.getTemplates,
  });
  const catId = tplQuery.data?.category_id ?? 25;
  const templates = useMemo(
    () => [...(tplQuery.data?.templates ?? [])].sort((a, b) => a.ordem - b.ordem),
    [tplQuery.data],
  );

  // Busca publicados e rascunhos da categoria do horóscopo.
  const pub = useArticles({ categories: [catId], status: 'publish', per_page: 70 });
  const dft = useArticles({ categories: [catId], status: 'draft', per_page: 70 });

  const articles = useMemo(() => {
    const all = [...(pub.data?.items ?? []), ...(dft.data?.items ?? [])];
    if (status === 'todos') return all;
    return all.filter((a) => a.status === status);
  }, [pub.data, dft.data, status]);

  const days: DaySlots[] = useMemo(() => {
    const byDay = new Map<string, ArticleSummary[]>();
    for (const a of articles) {
      const k = dayKey(a);
      if (!byDay.has(k)) byDay.set(k, []);
      byDay.get(k)!.push(a);
    }
    const keys = [...byDay.keys()].filter(Boolean).sort().reverse();
    return keys.map((key) => {
      const dayArticles = byDay.get(key)!;
      const used = new Set<number>();
      const slots = templates.map((tpl) => {
        const article =
          dayArticles.find((a) => !used.has(a.id) && matchTemplate(a, tpl)) ?? null;
        if (article) used.add(article.id);
        return { tpl, article };
      });
      const extras = dayArticles.filter((a) => !used.has(a.id));
      const published = dayArticles.filter((a) => a.status === 'publish').length;
      return { key, slots, extras, published };
    });
  }, [articles, templates]);

  const loading = tplQuery.isLoading || pub.isLoading || dft.isLoading;

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Horóscopo</h1>
          <p className={styles.subtitle}>
            Padrão diário de {templates.length} publicações. Cada dia segue os mesmos
            templates — rascunhos mostram a prévia, publicados mostram o conteúdo no ar.
          </p>
        </div>
        <div className={styles.headActions}>
          <div className={styles.modeBox}>
            <span className={styles.modeLabel}>Geração</span>
            <ModeSelector
              value={featuresQuery.data?.horoscope.mode ?? 'off'}
              onChange={(m) => setMode.mutate(m)}
              disabled={setMode.isPending || featuresQuery.isLoading}
              exclude={['semi']}
            />
          </div>
          <label className={styles.imgToggle} title="Gera uma imagem de capa por signo (custo extra)">
            <input
              type="checkbox"
              checked={featuresQuery.data?.horoscope.image ?? false}
              onChange={(e) => setImage.mutate(e.target.checked)}
              disabled={setImage.isPending}
            />
            <span>Imagem</span>
          </label>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => runDay.mutate()}
            loading={runDay.isPending}
          >
            Rodar do dia (preview)
          </Button>
          <Link to="/config/horoscopo" className={styles.tplLink}>
            Templates
          </Link>
        </div>
      </header>

      {runResult && (
        <div className={styles.runPanel}>
          <div className={styles.runHead}>
            <strong>Prévia do dia</strong> · {runResult.date} ·{' '}
            <span className={styles.muted}>
              {runResult.ok}/{runResult.count} signos
            </span>
            <button className={styles.runClose} onClick={() => setRunResult(null)}>×</button>
          </div>
          <div className={styles.runGrid}>
            {runResult.results.map((r) => (
              <div key={r.signo} className={r.ok ? styles.runItem : styles.runItemErr}>
                <span className={styles.runSigno}>{r.signo}</span>
                <span className={styles.runTitle}>{r.ok ? r.title : r.error}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.filters}>
        {STATUS_CHIPS.map((c) => (
          <button
            key={c.v}
            type="button"
            className={`${styles.chip} ${status === c.v ? styles.chipActive : ''}`}
            onClick={() => setStatus(c.v)}
          >
            {c.label}
          </button>
        ))}
        <div className={styles.spacer} />
        <span className={styles.count}>
          {loading ? 'a carregar…' : `${days.length} dias · ${articles.length} matérias`}
        </span>
      </div>

      {!loading && days.length === 0 && (
        <p className={styles.empty}>Nenhuma matéria de horóscopo com este filtro.</p>
      )}

      {days.map((day) => (
        <div key={day.key} className={styles.day}>
          <div className={styles.dayHead}>
            <h2 className={styles.dayTitle}>{fmtDay(day.key)}</h2>
            <span
              className={`${styles.dayCount} ${
                day.published >= templates.length ? styles.dayComplete : ''
              }`}
            >
              {day.published}/{templates.length} publicados
            </span>
          </div>

          <div className={styles.grid}>
            {day.slots.map(({ tpl, article }) => (
              <SlotCard key={tpl.key} tpl={tpl} article={article} />
            ))}
          </div>

          {day.extras.length > 0 && (
            <div className={styles.extras}>
              <span className={styles.extrasLabel}>Fora do padrão</span>
              {day.extras.map((a) => (
                <Link key={a.id} to={`/artigos/${a.id}`} className={styles.extraItem}>
                  <StatusBadge status={a.status} /> {a.title}
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'publish') return <Badge tone="ok" size="xs">no ar</Badge>;
  if (status === 'draft') return <Badge tone="warn" size="xs">rascunho</Badge>;
  return <Badge tone="neutral" size="xs">{status}</Badge>;
}

function SlotCard({
  tpl,
  article,
}: {
  tpl: HoroscopeTemplate;
  article: ArticleSummary | null;
}) {
  if (!article) {
    return (
      <div className={`${styles.card} ${styles.cardEmpty}`}>
        <span className={styles.cardType}>{tpl.label}</span>
        <span className={styles.missing}>em falta</span>
        <p className={styles.estrutura}>{tpl.estrutura}</p>
      </div>
    );
  }
  const preview = stripHtml(article.excerpt);
  return (
    <Link to={`/artigos/${article.id}`} className={styles.card}>
      <div className={styles.cardTop}>
        <span className={styles.cardType}>{tpl.label}</span>
        <StatusBadge status={article.status} />
      </div>
      <h3 className={styles.cardTitle}>{article.title}</h3>
      {preview ? (
        <p className={styles.preview}>{preview}</p>
      ) : (
        <p className={styles.previewEmpty}>
          {article.status === 'draft'
            ? 'Rascunho sem resumo — abrir para ver.'
            : 'Sem resumo.'}
        </p>
      )}
    </Link>
  );
}
